/**
 * Theme Toggle molecule component
 * Allows users to manually switch between light and dark themes
 */

// #region Imports
import { IconButton, Tooltip, Box, Menu, MenuItem, ListItemIcon, ListItemText, Typography } from "@mui/material";
import { Brightness4, Brightness7, SettingsBrightness, Check } from "@mui/icons-material";
import { useColorScheme } from "@mui/material/styles";
import { useState } from "react";
import { useTranslation } from "react-i18next";
// #endregion

// #region Types
/**
 * Theme mode options
 */
type ThemeMode = "light" | "dark" | "system";

/**
 * Theme option configuration
 */
interface ThemeOption {
    mode: ThemeMode;
    label: string;
    icon: React.ReactNode;
}

/**
 * Props for ThemeToggle molecule component
 */
interface ThemeToggleMoleculeProps {
    /** Optional test ID for testing purposes */
    "data-testid"?: string;
}
// #endregion

// #region Component
/**
 * ThemeToggle molecule component for switching between light, dark, and system themes
 * Uses MUI's useColorScheme hook for proper theme management with dropdown selection
 *
 * @param props - The component props
 * @returns JSX element representing the theme toggle dropdown
 */
function ThemeToggleMolecule({ ...props }: ThemeToggleMoleculeProps) {
    const { mode, setMode } = useColorScheme();
    const { t } = useTranslation("common");
    const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
    const open = Boolean(anchorEl);

    // Theme options configuration
    const themeOptions: ThemeOption[] = [
        {
            mode: "light",
            label: t("theme.light"),
            icon: <Brightness7 />,
        },
        {
            mode: "dark",
            label: t("theme.dark"),
            icon: <Brightness4 />,
        },
        {
            mode: "system",
            label: t("theme.system"),
            icon: <SettingsBrightness />,
        },
    ];

    const handleClick = (event: React.MouseEvent<HTMLElement>) => {
        setAnchorEl(event.currentTarget);
    };

    const handleClose = () => {
        setAnchorEl(null);
    };

    const handleThemeChange = (newMode: ThemeMode) => {
        setMode(newMode);
        handleClose();
    };

    // Get current theme option for display
    const currentOption = themeOptions.find(option => option.mode === mode) || themeOptions[0];

    return (
        <Box>
            <Tooltip title={t("theme.toggle")}>
                <IconButton
                    id="navbar-change-theme"
                    onClick={handleClick}
                    color="inherit"
                    aria-label="Change theme"
                    aria-controls={open ? "theme-menu" : undefined}
                    aria-haspopup="true"
                    aria-expanded={open ? "true" : undefined}
                    sx={[
                        {
                            display: "flex",
                            alignItems: "center",
                            gap: 0.5,
                            "&:hover": {
                                backgroundColor: "action.hover",
                            },
                            "&:focus": {
                                outline: "2px solid",
                                outlineColor: "primary.main",
                                outlineOffset: "2px",
                            },
                        },
                        theme =>
                            theme.applyStyles("dark", {
                                "&:hover": {
                                    backgroundColor: "rgba(255, 255, 255, 0.08)",
                                },
                            }),
                    ]}
                    {...props}
                >
                    {currentOption.icon}
                </IconButton>
            </Tooltip>

            <Menu
                id="theme-menu"
                anchorEl={anchorEl}
                open={open}
                onClose={handleClose}
                slotProps={{
                    list: { "aria-labelledby": "theme-button" },
                }}
                transformOrigin={{ horizontal: "right", vertical: "top" }}
                anchorOrigin={{ horizontal: "right", vertical: "bottom" }}
                sx={[
                    {
                        "& .MuiPaper-root": {
                            minWidth: 160,
                            mt: 1,
                            border: 1,
                            borderColor: "divider",
                        },
                    },
                    theme =>
                        theme.applyStyles("dark", {
                            "& .MuiPaper-root": {
                                borderColor: "rgba(255, 255, 255, 0.12)",
                                backgroundColor: "rgba(18, 18, 18, 0.95)",
                                backdropFilter: "blur(20px)",
                            },
                        }),
                ]}
            >
                {themeOptions.map(option => (
                    <MenuItem
                        key={option.mode}
                        onClick={() => handleThemeChange(option.mode)}
                        selected={option.mode === mode}
                        sx={[
                            {
                                minHeight: 40,
                                "&.Mui-selected": {
                                    backgroundColor: "action.selected",
                                },
                                "&:hover": {
                                    backgroundColor: "action.hover",
                                },
                            },
                            theme =>
                                theme.applyStyles("dark", {
                                    "&.Mui-selected": {
                                        backgroundColor: "rgba(255, 255, 255, 0.08)",
                                    },
                                    "&:hover": {
                                        backgroundColor: "rgba(255, 255, 255, 0.04)",
                                    },
                                }),
                        ]}
                    >
                        <ListItemIcon sx={{ minWidth: 36 }}>{option.icon}</ListItemIcon>
                        <ListItemText
                            primary={
                                <Typography
                                    variant="body2"
                                    sx={[
                                        {
                                            fontWeight: option.mode === mode ? 600 : 400,
                                        },
                                    ]}
                                >
                                    {option.label}
                                </Typography>
                            }
                        />
                        {option.mode === mode && (
                            <Check
                                sx={[
                                    {
                                        fontSize: "1rem",
                                        color: "primary.main",
                                        ml: 1,
                                    },
                                ]}
                            />
                        )}
                    </MenuItem>
                ))}
            </Menu>
        </Box>
    );
}
// #endregion

// #region Exports
export default ThemeToggleMolecule;
export type { ThemeToggleMoleculeProps };
// #endregion
